import { useEffect, useState } from "react";
import { Modal } from "antd";
import { CheckCircleFilled, DownOutlined } from "@ant-design/icons";
import rocketImg from "../../../../images/rocket.svg";
import ChantInput from "../../../../CommonComponents/ChantInput/ChantInput";
import ChantButton from "../../../../CommonComponents/ChantButton/ChantButton";
import MilestoneStatusBadge from "./MilestoneStatusBadge";
import useAdminDataStore, { selectCurrentGroupId } from "../../../../store/adminData/adminData";
import {
  fbGetReferrals,
  fbConfirmReferral,
} from "../../../../services/firebaseService/endPoints/admin/referrals";

const REFERRAL_COUNT = 5;
const REQUIRED_VERIFIED_COUNT = 2;

const emptyReferral = () => ({ name: "", confirmed: false, verifiedBySuperAdmin: false });

const MilestoneExpandCircle = () => {
  const groupId = useAdminDataStore(selectCurrentGroupId);
  const [referrals, setReferrals] = useState(
    Array.from({ length: REFERRAL_COUNT }, emptyReferral),
  );
  const [drafts, setDrafts] = useState({});
  const [confirmIndex, setConfirmIndex] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!groupId) return;
    fbGetReferrals(groupId).then(setReferrals);
  }, [groupId]);

  const handleDraftChange = (index, value) => {
    setDrafts((prev) => ({ ...prev, [index]: value }));
  };

  const handleTriggerConfirm = (index) => {
    const draft = (drafts[index] ?? "").trim();
    if (!draft || referrals[index].confirmed) return;
    setConfirmIndex(index);
  };

  const handleCancelConfirm = () => {
    if (confirmIndex !== null) {
      setDrafts((prev) => ({ ...prev, [confirmIndex]: "" }));
    }
    setConfirmIndex(null);
  };

  const handleConfirmIntroduction = () => {
    const index = confirmIndex;
    const name = (drafts[index] ?? "").trim();
    fbConfirmReferral(groupId, index, name)
      .then(() => {
        setReferrals((prev) => {
          const next = [...prev];
          next[index] = { ...next[index], name, confirmed: true };
          return next;
        });
      })
      .catch((err) => console.error("Failed to confirm referral:", err));
    setConfirmIndex(null);
  };

  const firstTwoFilled = referrals[0]?.confirmed && referrals[1]?.confirmed;
  const visibleCount = isExpanded ? REFERRAL_COUNT : 2;
  const verifiedCount = referrals.filter((r) => r.verifiedBySuperAdmin).length;
  const isCompleted = verifiedCount >= REQUIRED_VERIFIED_COUNT;

  return (
    <div className="milestone-card">
      <div className="milestone-card-top">
        <div className="milestone-card-header">
          <span className="milestone-number">Milestone 01</span>
          <MilestoneStatusBadge status={isCompleted ? "COMPLETE" : "IN PROGRESS"} />
        </div>
        <h3 className="milestone-title">Refer</h3>
        <p className="milestone-description">
          Bring two supporter groups into Chant and this milestone&apos;s done
          — plus every extra group you bring in after that unlocks a free year
          of your website.
        </p>
      </div>

      <div className="milestone-card-body">
        <p className="milestone-instruction-text">
          Reach out to your contact to share Chant then make an intro to our
          team at <a href="mailto:hello@chant.fan">hello@chant.fan</a>.
        </p>

        <div className="milestone-referral-row">
          {Array.from({ length: visibleCount }, (_, i) => {
            const referral = referrals[i];
            const isVerified = referral.verifiedBySuperAdmin;
            const hasDraft = !!(drafts[i] ?? "").trim();
            const displayValue = referral.confirmed
              ? referral.name
              : drafts[i] ?? referral.name;

            return (
              <div key={i} className="milestone-referral-item">
                <span className="milestone-referral-label">
                  Referral #{i + 1}
                  {referral.confirmed && (
                    <span
                      className={
                        isVerified
                          ? "milestone-referral-verified"
                          : "milestone-referral-in-progress"
                      }
                    >
                      {isVerified ? "Verified" : "In progress"}
                    </span>
                  )}
                </span>
                <ChantInput
                  placeholder="Enter here"
                  value={displayValue}
                  disabled={referral.confirmed}
                  onChange={(e) => handleDraftChange(i, e.target.value)}
                  suffix={
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        minWidth: 16,
                        height: 16,
                      }}
                    >
                      {isVerified ? (
                        <CheckCircleFilled style={{ color: "#22c55e", fontSize: 16 }} />
                      ) : referral.confirmed ? (
                        <span
                          style={{
                            color: "#22c55e",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          Saved
                        </span>
                      ) : hasDraft ? (
                        <span
                          onClick={() => handleTriggerConfirm(i)}
                          style={{
                            color: "var(--primary-background-darker)",
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          Save
                        </span>
                      ) : null}
                    </span>
                  }
                />
              </div>
            );
          })}
        </div>

        <div
          className={`milestone-promo-section ${firstTwoFilled ? "milestone-promo-section-clickable" : ""}`}
          onClick={firstTwoFilled ? () => setIsExpanded((prev) => !prev) : undefined}
        >
          <div className="milestone-promo-icon">
            <img src={rocketImg} alt="rocket" style={{ width: "60%", height: "60%", objectFit: "contain" }} />
          </div>
          <div className="milestone-promo-body">
            <p className="milestone-promo-text">
              Refer more and unlock a <strong>FREE website for 3 years</strong>
              <br />
              ($360 value)
            </p>
          </div>
          {firstTwoFilled && (
            <DownOutlined className={`milestone-promo-chevron ${isExpanded ? "milestone-promo-chevron-open" : ""}`} />
          )}
        </div>
      </div>

      <Modal
        open={confirmIndex !== null}
        title="Confirm Introduction"
        onCancel={handleCancelConfirm}
        footer={[
          <ChantButton key="cancel" type="secondary" onClick={handleCancelConfirm}>
            Cancel
          </ChantButton>,
          <ChantButton key="confirm" type="primary" onClick={handleConfirmIntroduction}>
            Confirmed
          </ChantButton>,
        ]}
      >
        <p>
          Please confirm that you have reached out to the group to share
          Chant and made an introduction to our team (
          <a href="mailto:hello@chant.fan">hello@chant.fan</a>).
        </p>
        <p>Thanks for the referral and helping Chant grow!</p>
      </Modal>
    </div>
  );
};

export default MilestoneExpandCircle;
